// src/features/inventory/pages/SupplierDetailPage/tabs/Items/index.jsx
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useItemFiltering } from '../../../../hooks/useItemFiltering';
import { useProductGroups } from '../../../../hooks/useProductGroups';
import { useProductGroupPermissions } from '../../../../../auth/hooks/usePermissions';

export default function Items({ supplierId }) {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();

  const {
    items,
    loading,
    searchTerm,
    setSearchTerm,
    productGroupId,
    setProductGroupId,
  } = useItemFiltering({ supplierId });

  const hasActiveFilters = searchTerm !== '' || productGroupId !== '';
  const clearFilters = () => { setSearchTerm(''); setProductGroupId(''); };

  const { productGroups, fetchProductGroups } = useProductGroups();
  const { canRead: canReadGroups } = useProductGroupPermissions();

  useEffect(() => {
    if (canReadGroups) fetchProductGroups();
  }, [fetchProductGroups, canReadGroups]);

  // Flatten each item with the supplier-specific link fields for this supplier
  const rows = useMemo(() =>
    items.map((item) => {
      const link = item.supplier_items?.find((si) => si.supplier_id === supplierId) ?? {};
      return {
        ...item,
        supplier_item_number: link.supplier_item_number ?? null,
        barcode: link.barcode ?? null,
        sort_order: link.sort_order ?? 0,
      };
    }),
    [items, supplierId]
  );

  const columns = useMemo(() => [
    {
      field: 'sku',
      headerName: t('inventory:items.grid.sku'),
      width: 140,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" variant="outlined" color="info" />
      ),
    },
    {
      field: 'supplier_item_number',
      headerName: t('inventory:items.tabs.suppliers.supplier_sku'),
      width: 140,
      renderCell: ({ value }) => value
        ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{value}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      field: 'name',
      headerName: t('inventory:items.grid.name'),
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'barcode',
      headerName: t('inventory:items.tabs.suppliers.barcode'),
      width: 140,
      renderCell: ({ value }) => value
        ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{value}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      field: 'last_stock_transaction',
      headerName: t('inventory:items.detail.current_stock'),
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const tx = params.row.last_stock_transaction;
        if (!tx) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return <Typography variant="body2">{tx.quantity_after} {params.row.uom}</Typography>;
      },
    },
    {
      field: 'active',
      headerName: t('inventory:items.grid.active'),
      width: 110,
      sortable: false,
      renderCell: ({ value }) => (
        <Chip
          label={value ? t('common:status.active') : t('common:status.inactive')}
          color={value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ], [t]);

  const isEmpty = !loading && rows.length === 0;

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Filter bar */}
      <Box m={3} display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t('inventory:items.filter.search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {canReadGroups && <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="items-product-group-label">{t('inventory:items.grid.product_group')}</InputLabel>
          <Select labelId="items-product-group-label"
            value={productGroupId}
            onChange={(e) => setProductGroupId(e.target.value)}
            label={t('inventory:items.grid.product_group')}
          >
            <MenuItem value="">{t('inventory:items.filter.all_groups')}</MenuItem>
            {productGroups.map((g) => (
              <MenuItem key={g.product_group_id} value={g.product_group_id}>
                {g.code} · {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>}

        {hasActiveFilters && (
          <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
            {t('inventory:items.filter.clear_filters')}
          </Button>
        )}
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading && isEmpty && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('inventory:suppliers.detail.tabs.items.empty')}
          </Typography>
        </Box>
      )}

      {!loading && !isEmpty && (
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.item_id}
          onRowClick={(params) => navigate(`/inventory/items/${params.row.item_id}`)}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{
            flex: 1,
            border: 'none',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': { alignItems: 'center' },
          }}
        />
      )}
    </Box>
  );
}
